// CostTrends + RateCharts — haulage & dump cost movement over time
// (Suresh 2026-10-06). Mirrors the manual "Working Papers": total cost
// per load = haulage (hauler x destination) + tip fee (dump site, flat
// or per-tonne), both date-effective via the rate charts.
import { useState, useEffect } from 'react';
import { fetchJson } from '../api.js';
import { BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList } from 'recharts';

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

// Pivot [{month, <nameKey>, <valKey>}] into { months, rows: [{name, byMonth, total}] }
function pivot(rows, nameKey, valKey) {
  const months = Array.from(new Set(rows.map(r => r.month))).sort();
  const byName = new Map();
  for (const r of rows) {
    const e = byName.get(r[nameKey]) || { name: r[nameKey], byMonth: {}, byMonthRated: {}, total: 0, loads: 0, unrated: 0 };
    e.byMonth[r.month] = Number(r[valKey]) || 0;
    e.byMonthRated[r.month] = Math.max(0, (r.loads || 0) - (r.unrated || 0));
    e.total += Number(r[valKey]) || 0;
    e.loads += r.loads || 0;
    e.unrated += r.unrated || 0;
    byName.set(r[nameKey], e);
  }
  // Providers with $0 across the whole window (no rated activity) are
  // noise — drop them (Suresh 2026-10-06). They reappear the moment a
  // rate gives them a nonzero cost.
  const out = Array.from(byName.values())
    .filter(r => r.total > 0)
    .sort((a, b) => b.total - a.total);
  return { months, rows: out };
}

function PivotTable({ title, data, unit, showAvg, monthlyAvgRows }) {
  if (!data || data.rows.length === 0) return null;
  const { months, rows } = data;
  const totLoads = rows.reduce((s, r) => s + (r.loads || 0), 0);
  const totCost = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', overflowX: 'auto' }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <table className="fs-table tidy" style={{ fontSize: '0.85em' }}>
        <thead><tr>
          <th style={{ position: 'sticky', left: 0, background: '#fff' }}>Provider</th>
          {months.map(m => <th key={m} className="num">{m}</th>)}
          <th className="num">Total</th>
          {showAvg && <th className="num">Loads</th>}
          {showAvg && <th className="num" title="Total cost ÷ rated loads for this provider over the window">Avg $/load</th>}
        </tr></thead>
        <tbody>
          {rows.flatMap(r => {
            const ratedLoads = (r.loads || 0) - (r.unrated || 0);
            const out = [(
              <tr key={r.name}>
                <td style={{ fontWeight: 600, position: 'sticky', left: 0, background: '#fff', whiteSpace: 'nowrap' }}>
                  {r.name}{r.unrated > 0 && <span style={{ color: '#c05100' }} title={r.unrated + ' load(s) have no rate on the chart — excluded from totals'}> ⚠</span>}
                </td>
                {months.map(m => (
                  <td key={m} className="num">{r.byMonth[m] != null && r.byMonth[m] !== 0 ? money(r.byMonth[m]) : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                ))}
                <td className="num" style={{ fontWeight: 700 }}>{money(r.total)}</td>
                {showAvg && <td className="num">{(r.loads || 0).toLocaleString()}</td>}
                {showAvg && <td className="num" style={{ fontWeight: 700, color: '#0f766e' }}>{ratedLoads > 0 ? money(r.total / ratedLoads) : '—'}</td>}
              </tr>
            )];
            if (monthlyAvgRows) {
              // Total-then-average in the SAME table (Suresh): a lighter
              // sub-row with that provider's monthly avg $/load.
              out.push(
                <tr key={r.name + '-avg'} style={{ background: '#f8fafc', color: '#0f766e', fontSize: '0.92em' }}>
                  <td style={{ position: 'sticky', left: 0, background: '#f8fafc', paddingLeft: '1.2rem', fontStyle: 'italic', whiteSpace: 'nowrap' }}>↳ avg $/load</td>
                  {months.map(m => {
                    const rated = (r.byMonthRated && r.byMonthRated[m]) || 0;
                    const tot = r.byMonth[m] || 0;
                    return <td key={m} className="num">{rated > 0 && tot ? money(tot / rated) : <span style={{ color: '#cbd5e1' }}>—</span>}</td>;
                  })}
                  <td className="num" style={{ fontWeight: 600 }}>{ratedLoads > 0 ? money(r.total / ratedLoads) : '—'}</td>
                  {showAvg && <td className="num"></td>}
                  {showAvg && <td className="num"></td>}
                </tr>
              );
            }
            return out;
          })}
          <tr style={{ borderTop: '2px solid #94a3b8', fontWeight: 700, background: '#f8fafc' }}>
            <td style={{ position: 'sticky', left: 0, background: '#f8fafc' }}>TOTAL {unit}</td>
            {months.map(m => <td key={m} className="num">{money(rows.reduce((s, r) => s + (r.byMonth[m] || 0), 0))}</td>)}
            <td className="num">{money(totCost)}</td>
            {showAvg && <td className="num">{totLoads.toLocaleString()}</td>}
            {showAvg && <td className="num">{totLoads > 0 ? money(totCost / Math.max(1, totLoads - rows.reduce((s, r) => s + (r.unrated || 0), 0))) : '—'}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}


// Merged haulage table (Suresh 2026-10-06): haulers NESTED under the
// dump site they serve. Each month cell: avg $/load with (total) in
// brackets. Zero-cost providers dropped.
function kfmt(n) {
  n = Number(n) || 0;
  return n >= 100000 ? '$' + (n / 1000).toFixed(0) + 'K' : '$' + Math.round(n).toLocaleString();
}
function NestedHaulageTable({ bySiteHauler }) {
  const rows = bySiteHauler || [];
  if (rows.length === 0) return null;
  const months = Array.from(new Set(rows.map(r => r.month))).sort();
  const sites = new Map();   // site -> { byMonth:{m:{cost,rated}}, cost, loads, rated, haulers: Map }
  for (const r of rows) {
    const site = sites.get(r.site) || { byMonth: {}, cost: 0, loads: 0, rated: 0, haulers: new Map() };
    const rated = Math.max(0, r.loads - (r.unrated || 0));
    const cost = Number(r.haulage) || 0;
    const bm = site.byMonth[r.month] || { cost: 0, rated: 0 };
    bm.cost += cost; bm.rated += rated; site.byMonth[r.month] = bm;
    site.cost += cost; site.loads += r.loads; site.rated += rated;
    const h = site.haulers.get(r.hauler) || { byMonth: {}, cost: 0, loads: 0, rated: 0 };
    const hbm = h.byMonth[r.month] || { cost: 0, rated: 0 };
    hbm.cost += cost; hbm.rated += rated; h.byMonth[r.month] = hbm;
    h.cost += cost; h.loads += r.loads; h.rated += rated;
    site.haulers.set(r.hauler, h);
    sites.set(r.site, site);
  }
  const cell = (bm, bold) => {
    if (!bm || bm.cost <= 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
    const avg = bm.rated > 0 ? bm.cost / bm.rated : null;
    return (
      <span style={bold ? { fontWeight: 700 } : undefined}>
        {avg != null ? money(avg) : '—'}
        <span style={{ color: '#94a3b8', fontSize: '0.85em' }}> ({kfmt(bm.cost)})</span>
      </span>
    );
  };
  const siteList = Array.from(sites.entries()).filter(([, v]) => v.cost > 0).sort((a, b) => b[1].cost - a[1].cost);
  const grand = { cost: 0, loads: 0, rated: 0, byMonth: {} };
  for (const [, v] of siteList) {
    grand.cost += v.cost; grand.loads += v.loads; grand.rated += v.rated;
    for (const m of months) {
      const bm = v.byMonth[m]; if (!bm) continue;
      const g = grand.byMonth[m] || { cost: 0, rated: 0 };
      g.cost += bm.cost; g.rated += bm.rated; grand.byMonth[m] = g;
    }
  }
  return (
    <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', overflowX: 'auto' }}>
      <h3 style={{ marginTop: 0 }}>🏗🚚 Haulage cost — dump site → trucking company (avg $/load, total in brackets)</h3>
      <table className="fs-table tidy" style={{ fontSize: '0.83em' }}>
        <thead><tr>
          <th style={{ position: 'sticky', left: 0, background: '#fff' }}>Site / Carrier</th>
          {months.map(m => <th key={m} className="num">{m}</th>)}
          <th className="num">Total</th>
          <th className="num">Loads</th>
          <th className="num">Avg $/load</th>
        </tr></thead>
        <tbody>
          {siteList.flatMap(([siteName, v]) => {
            const out = [(
              <tr key={siteName} style={{ background: '#eef2ff' }}>
                <td style={{ fontWeight: 700, position: 'sticky', left: 0, background: '#eef2ff', whiteSpace: 'nowrap' }}>{siteName}</td>
                {months.map(m => <td key={m} className="num">{cell(v.byMonth[m], true)}</td>)}
                <td className="num" style={{ fontWeight: 700 }}>{kfmt(v.cost)}</td>
                <td className="num" style={{ fontWeight: 700 }}>{v.loads.toLocaleString()}</td>
                <td className="num" style={{ fontWeight: 700, color: '#0f766e' }}>{v.rated > 0 ? money(v.cost / v.rated) : '—'}</td>
              </tr>
            )];
            for (const [hName, h] of Array.from(v.haulers.entries()).filter(([, x]) => x.cost > 0).sort((a, b) => b[1].cost - a[1].cost)) {
              out.push(
                <tr key={siteName + '|' + hName}>
                  <td style={{ position: 'sticky', left: 0, background: '#fff', paddingLeft: '1.4rem', whiteSpace: 'nowrap' }}>↳ {hName}</td>
                  {months.map(m => <td key={m} className="num">{cell(h.byMonth[m])}</td>)}
                  <td className="num">{kfmt(h.cost)}</td>
                  <td className="num">{h.loads.toLocaleString()}</td>
                  <td className="num" style={{ color: '#0f766e', fontWeight: 600 }}>{h.rated > 0 ? money(h.cost / h.rated) : '—'}</td>
                </tr>
              );
            }
            return out;
          })}
          <tr style={{ borderTop: '2px solid #94a3b8', fontWeight: 700, background: '#f8fafc' }}>
            <td style={{ position: 'sticky', left: 0, background: '#f8fafc' }}>TOTAL haulage</td>
            {months.map(m => <td key={m} className="num">{cell(grand.byMonth[m], true)}</td>)}
            <td className="num">{kfmt(grand.cost)}</td>
            <td className="num">{grand.loads.toLocaleString()}</td>
            <td className="num" style={{ color: '#0f766e' }}>{grand.rated > 0 ? money(grand.cost / grand.rated) : '—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const REPORT_SECTIONS = [
  ['chart',   'Avg haulage by load type (chart)'],
  ['avg',     'Average $/load — site & carrier table'],
  ['total',   'Total cost — site & carrier table'],
  ['tips',    'Tip fees by dump site'],
  ['monthly', 'Monthly totals by component']
];

export function CostTrends({ costTrends, months = 12 }) {
  // Report builder: pick which sections go in the PDF.
  const [showExport, setShowExport] = useState(false);
  const [sections, setSections] = useState(() => new Set(REPORT_SECTIONS.map(([k]) => k)));
  const toggleSection = (k) => setSections(prev => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  if (!costTrends) {
    return (
      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#fffbeb', border: '1px solid #fcd34d' }}>
        <strong>Cost trends unavailable</strong> — apply db/schema-049-rate-charts.sql in Supabase to enable haulage/tip costing.
      </div>
    );
  }
  const monthly = costTrends.monthly || [];
  const unratedHaul = monthly.reduce((s, m) => s + (m.unrated_haul || 0), 0);
  const unratedTip = monthly.reduce((s, m) => s + (m.unrated_tip || 0), 0);
  // Average disposal cost per load, by month. Each component's average
  // divides by its RATED loads so missing rates don't drag the line down.
  // Avg HAULAGE $/load by material (dump fees excluded) — Suresh.
  const matRows = costTrends.materialMonthlyHaul || [];
  const matNames = Array.from(new Set(matRows.map(r => r.material)));
  const MAT_COLORS = ['#0e7490', '#b45309', '#15803d', '#b91c1c', '#1d4ed8'];
  const matAvgMonthly = Array.from(new Set(matRows.map(r => r.month))).sort().map(month => {
    const row = { month };
    for (const mn of matNames) {
      const r = matRows.find(x => x.month === month && x.material === mn);
      const rated = r ? Math.max(0, r.loads - (r.unrated || 0)) : 0;
      row[mn] = r && rated > 0 ? Math.round(Number(r.haulage) / rated) : null;
    }
    return row;
  });
  const avgMonthly = monthly.map(m => {
    const ratedH = Math.max(0, (m.loads || 0) - (m.unrated_haul || 0));
    const ratedT = Math.max(0, (m.loads || 0) - (m.unrated_tip || 0));
    const avgHaul = ratedH > 0 ? Number(m.haulage) / ratedH : null;
    const avgTip = ratedT > 0 ? Number(m.tip) / ratedT : null;
    return {
      month: m.month,
      avgHaul: avgHaul != null ? Math.round(avgHaul) : null,
      avgTip: avgTip != null ? Math.round(avgTip) : null,
      avgTotal: (avgHaul != null || avgTip != null) ? Math.round((avgHaul || 0) + (avgTip || 0)) : null
    };
  });
  return (
    <>
      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>💸 Total disposal cost by component</h3>
        {(unratedHaul > 0 || unratedTip > 0) && (
          <div className="small" style={{ color: '#c05100', marginBottom: '0.4rem' }}>
            ⚠ {unratedHaul} load(s) missing a haulage rate, {unratedTip} missing a tip rate — those components are excluded, totals understate. Fix in the Rate charts below.
          </div>
        )}
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="cost" tickFormatter={(v) => '$' + (v / 1000).toFixed(0) + 'K'} />
            <YAxis yAxisId="loads" orientation="right" allowDecimals={false}
                   tickFormatter={(v) => v.toLocaleString()} />
            <Tooltip formatter={(v, name) => name === 'Incoming loads (tips)' ? v.toLocaleString() : money(v)} />
            <Legend />
            <Bar yAxisId="cost" dataKey="haulage" name="Haulage (trucking)" stackId="c" fill="#0e7490" />
            <Bar yAxisId="cost" dataKey="tip" name="Tip fees (dump sites)" stackId="c" fill="#7c3aed" />
            <Line yAxisId="loads" type="monotone" dataKey="incoming_loads" name="Incoming loads (tips)"
                  stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="note">Orange line = incoming hydrovac loads (right axis) — disposal cost should track it; divergence means rate movement or mix shift.</div>
        <table className="fs-table tidy" style={{ fontSize: '0.85em', marginTop: '0.5rem' }}>
          <thead><tr><th>Month</th><th className="num">Loads</th><th className="num">Haulage $</th><th className="num">Tip $</th><th className="num">Total $</th><th className="num">$/load</th></tr></thead>
          <tbody>
            {monthly.map(m => (
              <tr key={m.month}>
                <td>{m.month}</td>
                <td className="num">{m.loads}</td>
                <td className="num">{money(m.haulage)}</td>
                <td className="num">{money(m.tip)}</td>
                <td className="num" style={{ fontWeight: 700 }}>{money(m.total)}</td>
                <td className="num">{m.loads > 0 ? money(m.total / m.loads) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>📉 Average haulage cost per load — by load type (dump fees excluded)</h3>
          <div style={{ position: 'relative' }}>
            <button className="refresh-btn small" style={{ background: '#0f766e', color: '#fff' }}
                    onClick={() => setShowExport(o => !o)}>
              📄 Export PDF…
            </button>
            {showExport && (
              <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 30, background: '#fff',
                            border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.15)',
                            padding: '0.7rem 0.9rem', width: 290 }}>
                <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>Report contents</div>
                {REPORT_SECTIONS.map(([k, label]) => (
                  <label key={k} className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sections.has(k)} onChange={() => toggleSection(k)} />
                    {label}
                  </label>
                ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: '0.5rem' }}>
                  <button className="refresh-btn small" style={{ background: '#94a3b8' }} onClick={() => setShowExport(false)}>Cancel</button>
                  <button className="refresh-btn small" style={{ background: '#0f766e' }} disabled={sections.size === 0}
                          onClick={() => {
                            window.open('/api/loads-analytics/export?months=' + months + '&sections=' + Array.from(sections).join(','), '_blank');
                            setShowExport(false);
                          }}>
                    Generate ({sections.size})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="small" style={{ color: '#666', marginBottom: '0.3rem' }}>
          Averages divide by rated loads. Watch these lines for rate creep per material.
        </div>
        {matAvgMonthly.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={matAvgMonthly} margin={{ top: 22, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(v) => '$' + v} domain={['auto', 'auto']} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              {matNames.map((mn, i) => (
                <Line key={mn} type="monotone" dataKey={mn} name={mn} stroke={MAT_COLORS[i % MAT_COLORS.length]} strokeWidth={1.8} dot={{ r: 3 }} connectNulls>
                  <LabelList dataKey={mn} position="top" offset={8}
                             formatter={(v) => v != null ? '$' + Number(v).toLocaleString() : ''}
                             style={{ fontSize: 10, fontWeight: 600, fill: MAT_COLORS[i % MAT_COLORS.length] }} />
                </Line>
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <NestedHaulageTable bySiteHauler={costTrends.bySiteHauler} />
      <PivotTable title="🏗 Tip fees by dump site (monthly)" data={pivot(costTrends.bySite || [], 'site', 'tip')} unit="tip fees" showAvg />
    </>
  );
}

export function RateCharts({ isAdmin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  async function load() {
    try { setErr(null); setData(await fetchJson('/api/rate-charts')); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { if (open) load(); }, [open]);

  async function save(chart, row) {
    setBusy(true);
    try {
      const r = await fetch('/api/rate-charts', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chart, row })
      });
      if (!r.ok) throw new Error(await r.text());
      await load();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }
  async function del(chart, id) {
    if (!confirm('Delete this rate row? Loads in its window will show as unrated until replaced.')) return;
    setBusy(true);
    try {
      const r = await fetch('/api/rate-charts/' + id + '?chart=' + chart, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(await r.text());
      await load();
    } catch (e) { alert('Delete failed: ' + e.message); }
    finally { setBusy(false); }
  }
  function promptRow(chart, existing) {
    const row = existing ? { ...existing } : {};
    if (chart === 'haul') {
      row.hauler = window.prompt('Hauler (company, no #unit):', row.hauler || ''); if (row.hauler == null) return;
      row.destination = window.prompt('Destination (dump site):', row.destination || ''); if (row.destination == null) return;
      row.rate_per_load = window.prompt('Rate per load ($):', row.rate_per_load || ''); if (row.rate_per_load == null) return;
    } else {
      row.site = window.prompt('Dump site:', row.site || ''); if (row.site == null) return;
      row.rate_type = window.prompt("Rate type: 'flat' or 'per_tonne':", row.rate_type || 'flat'); if (row.rate_type == null) return;
      row.rate = window.prompt('Rate ($ per load, or $ per tonne):', row.rate || ''); if (row.rate == null) return;
    }
    row.effective_from = window.prompt('Effective from (YYYY-MM-DD):', row.effective_from || ''); if (row.effective_from == null) return;
    row.effective_to = window.prompt('Effective to (YYYY-MM-DD, blank = ongoing):', row.effective_to || ''); if (row.effective_to == null) return;
    row.notes = window.prompt('Notes:', row.notes || ''); if (row.notes == null) return;
    save(chart, row);
  }

  return (
    <div className="card" style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>📋 Rate charts — haulage & dump sites</h3>
        <button className="refresh-btn small" onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <>
          {err && <div className="error small" style={{ marginTop: '0.4rem' }}>{err}</div>}
          {!data && !err && <div className="small" style={{ color: '#888', marginTop: '0.4rem' }}>Loading…</div>}
          {data && data.gaps && (data.gaps.haul.length > 0 || data.gaps.dump.length > 0) && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.5rem 0.75rem', margin: '0.6rem 0' }}>
              <div style={{ fontWeight: 700, color: '#b91c1c', marginBottom: '0.3rem' }}>
                ⚠ Missing rates — these loads are UNCOSTED until a rate is added:
              </div>
              {data.gaps.haul.map(g => (
                <div key={'h' + g.hauler + g.destination} className="small" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ flex: 1 }}>
                    <strong>{g.hauler}</strong> → {g.destination} · {g.loads} load(s) · {g.first_load} to {g.last_load} · no haulage rate
                  </span>
                  {isAdmin && <button className="refresh-btn small" onClick={() => promptRow('haul', { hauler: g.hauler, destination: g.destination, effective_from: g.first_load })}>+ Add rate</button>}
                </div>
              ))}
              {data.gaps.dump.map(g => (
                <div key={'d' + g.site} className="small" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ flex: 1 }}>
                    <strong>{g.site}</strong> · {g.loads} load(s) · {g.first_load} to {g.last_load} · no tip rate
                  </span>
                  {isAdmin && <button className="refresh-btn small" onClick={() => promptRow('dump', { site: g.site, effective_from: g.first_load })}>+ Add rate</button>}
                </div>
              ))}
            </div>
          )}
          {data && data.gaps && data.gaps.haul.length === 0 && data.gaps.dump.length === 0 && (
            <div className="small" style={{ color: '#15803d', marginTop: '0.4rem' }}>Every load in the last 12 months has both rates. ✓</div>
          )}
          {data && (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
              <div style={{ flex: '1 1 460px' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
                  Haulage ($/load, per hauler → destination)
                  {isAdmin && <button className="refresh-btn small" style={{ marginLeft: 8 }} disabled={busy} onClick={() => promptRow('haul', null)}>+ Add</button>}
                </div>
                <table className="fs-table tidy" style={{ fontSize: '0.85em' }}>
                  <thead><tr><th>Hauler</th><th>Destination</th><th className="num">$/load</th><th>From</th><th>To</th>{isAdmin && <th></th>}</tr></thead>
                  <tbody>
                    {(data.haul || []).map(r => (
                      <tr key={r.id} style={!r.effective_to ? undefined : { opacity: 0.6 }}>
                        <td>{r.hauler}</td><td>{r.destination}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{money(r.rate_per_load)}</td>
                        <td className="small">{r.effective_from}</td>
                        <td className="small">{r.effective_to || 'ongoing'}</td>
                        {isAdmin && <td>
                          <button className="refresh-btn small" disabled={busy} onClick={() => promptRow('haul', r)}>Edit</button>{' '}
                          <button className="refresh-btn small" style={{ background: '#b91c1c' }} disabled={busy} onClick={() => del('haul', r.id)}>✕</button>
                        </td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ flex: '1 1 420px' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
                  Dump sites (tip fee)
                  {isAdmin && <button className="refresh-btn small" style={{ marginLeft: 8 }} disabled={busy} onClick={() => promptRow('dump', null)}>+ Add</button>}
                </div>
                <table className="fs-table tidy" style={{ fontSize: '0.85em' }}>
                  <thead><tr><th>Site</th><th>Type</th><th className="num">Rate</th><th>From</th><th>To</th>{isAdmin && <th></th>}</tr></thead>
                  <tbody>
                    {(data.dump || []).map(r => (
                      <tr key={r.id} style={!r.effective_to ? undefined : { opacity: 0.6 }}>
                        <td>{r.site}</td>
                        <td className="small">{r.rate_type === 'per_tonne' ? '$/tonne' : 'flat $/load'}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{money(r.rate)}</td>
                        <td className="small">{r.effective_from}</td>
                        <td className="small">{r.effective_to || 'ongoing'}</td>
                        {isAdmin && <td>
                          <button className="refresh-btn small" disabled={busy} onClick={() => promptRow('dump', r)}>Edit</button>{' '}
                          <button className="refresh-btn small" style={{ background: '#b91c1c' }} disabled={busy} onClick={() => del('dump', r.id)}>✕</button>
                        </td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="note" style={{ marginTop: '0.4rem' }}>
            Rate changes: close the old row (set its To date) and add a new row from the change date. Seeded from the Aug 2026 Working Papers (Dec 2024 – Aug 2026 observed rates).
          </div>
        </>
      )}
    </div>
  );
}
